/**
 * Orari i punonjësve — dy nivele, dhe i dyti VETËM SHIKIM.
 *
 *   View 1 · `viewMode === 'overview'`  — lista e të gjithë punonjësve
 *   View 2 · `viewMode === 'individual'` — «Orari Individual Mujor» i njërit
 *
 * View 2 hapet me klikim mbi një person te View 1 dhe nuk ndryshon asgjë: pa
 * krijim, pa editim, pa fshirje, pa zvarritje, pa ndryshim turni. Kontrata me
 * rrjetën është e shprehur — `readOnly`, jo thjesht mungesë e handler-ave: një
 * ekran që s'ka veprime, por ka butona veprimi, gënjen.
 *
 * Prandaj këtu nuk gjenden më dialogu i korrigjimit të ditës, ai i slot-it dhe
 * rrjeta e detajuar e orëve. Korrigjimet bëhen aty ku kanë vendin — te Qendra e
 * Aprovimeve (`/inbox?create=attendance_correction`) — dhe kalojnë nga një
 * aprovim, jo nga një klikim mbi orarin e dikujt.
 *
 * Filtri «Departamenti» rri vetëm te View 1: te View 2 punonjësi është zgjedhur
 * tashmë, dhe një filtër departamenti aty do të nënkuptonte një listë që s'ka.
 *
 * Kthimi ruan kontekstin e View 1 — muaji, viti, projekti dhe departamenti nuk
 * preken nga `backToOverview()`; pastrohet vetëm punonjësi dhe të dhënat e tij.
 *
 * Auto-load: ngarkohet automatikisht kur zgjidhet punëtori ose ndryshohet muaji/viti.
 */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Button, TextField, MenuItem,
  CircularProgress, Tooltip, Divider, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, TableSortLabel, Chip,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import DownloadIcon      from '@mui/icons-material/Download';
import PictureAsPdfIcon  from '@mui/icons-material/PictureAsPdf';
import PrintIcon         from '@mui/icons-material/Print';
import RestartAltIcon    from '@mui/icons-material/RestartAlt';
import ChevronLeftIcon   from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon  from '@mui/icons-material/ChevronRight';
import ArrowBackIcon     from '@mui/icons-material/ArrowBack';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PeopleIcon        from '@mui/icons-material/People';
import PersonIcon        from '@mui/icons-material/Person';
import WorkScheduleMatrix from './WorkScheduleMatrix';
import * as XLSX          from 'xlsx-js-style';
import jsPDF              from 'jspdf';
import autoTable          from 'jspdf-autotable';
import { useTranslation } from 'react-i18next';
import usePermissions from '../../hooks/usePermissions';
import { wasEmployedOn } from '../../utils/workerStatus';

// ── Constants ─────────────────────────────────────────────────────────────────

const API_URL   = process.env.REACT_APP_API_URL || '';
const THIS_YEAR = new Date().getFullYear();
const THIS_MON  = new Date().getMonth() + 1;
const YEARS     = Array.from({ length: 5 }, (_, i) => THIS_YEAR - 2 + i);


// ── Helpers ───────────────────────────────────────────────────────────────────

// Token canonical sources: sessionStorage (primary) + cookie (rememberMe).
// Mirrors apiServices.js so all callers share the same auth source.
function readStoredUser() {
  try {
    const raw = sessionStorage.getItem('logedInUser') || '';
    if (raw) return JSON.parse(raw);
    const cookie = document.cookie.split(';').find(c => c.trim().startsWith('logedInUser='));
    if (cookie) {
      const val = decodeURIComponent(cookie.split('=').slice(1).join('='));
      return JSON.parse(val);
    }
  } catch { /* ignore */ }
  return {};
}

function getAuthHeaders() {
  const t = readStoredUser()?.token || '';
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function getCurrentUser() {
  return readStoredUser();
}

function fmtHM(min) {
  const m = Math.max(0, Math.round(Number(min) || 0));
  return `${Math.floor(m / 60)}:${('0' + (m % 60)).slice(-2)}`;
}

function fmtDiff(min) {
  if (min == null || isNaN(Number(min))) return '—';
  const n = Number(min), abs = Math.abs(n);
  return `${n >= 0 ? '+' : '-'}${fmtHM(abs)}`;
}

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit' });
}

function workerFullName(w) {
  if (!w) return '';
  return `${w.first_name || w.FirstName || ''} ${w.last_name || w.LastName || ''}`.trim();
}

// Local clock-minutes (0–1439) from an ISO timestamp.
function isoMin(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d) ? null : d.getHours() * 60 + d.getMinutes();
}

// Minutes of [s, e] (absolute minutes, e may exceed 1440 when crossing midnight)
// that fall inside the night window 22:00–06:00.
function nightOverlap(s, e) {
  const lap = (a, b) => Math.max(0, Math.min(e, b) - Math.max(s, a));
  let total = 0;
  for (let base = 0; base <= 1440; base += 1440) {
    total += lap(base + 0,    base + 360);   // 00:00–06:00
    total += lap(base + 1320, base + 1440);  // 22:00–24:00
  }
  return total;
}

// Net night-work minutes for a day row: worked span (check-in → check-out)
// intersected with the night window, minus any break that falls in it.
function nightMinutes(row) {
  if (!row || !row.check_in || !row.check_out) return 0;
  const ci = isoMin(row.check_in);
  let   co = isoMin(row.check_out);
  if (ci == null || co == null) return 0;
  if (co < ci) co += 1440;
  let night = nightOverlap(ci, co);
  for (const br of (row.breaks || [])) {
    if (!br.start || !br.end) continue;
    let bs = isoMin(br.start), be = isoMin(br.end);
    if (bs == null || be == null) continue;
    if (be < bs) be += 1440;
    night -= nightOverlap(bs, be);
  }
  return Math.max(0, night);
}

// ── Sample preview data ───────────────────────────────────────────────────────
// Rendered when no worker is selected, so the user can see what the report
// looks like. Two ISO weeks (Mon 02.03 → Sun 15.03 2026) with a mix of regular
// days, a late check-in, an OT day, an absence, an adjusted day and weekend work.

const SAMPLE_DAY_SHORT = ['Di', 'Hë', 'Ma', 'Më', 'En', 'Pr', 'Sh']; // 0=Sun, 1=Mon, …
const SAMPLE_SCHED_START = '08:00';
const SAMPLE_SCHED_END   = '16:00';

function iso(date, hhmm) {
  return `${date}T${hhmm}:00`;
}

function sampleRow(date, opts = {}) {
  const dow = new Date(`${date}T00:00:00`).getDay(); // 0..6 (Sun..Sat)
  const isWeekend = dow === 0 || dow === 6;
  const base = {
    date,
    day_short: SAMPLE_DAY_SHORT[dow],
    schedule_start: SAMPLE_SCHED_START,
    schedule_end:   SAMPLE_SCHED_END,
    is_work_day: !isWeekend,
    is_adjusted: false,
    compliance_status: 'present',
    check_in:  null,
    check_out: null,
    breaks: [],
    planned_minutes:     isWeekend ? 0 : 450,
    net_worked_minutes:  0,
    overtime_minutes:    0,
    break_minutes:       0,
    late_minutes:        0,
  };
  if (opts.absent)  return { ...base, compliance_status: 'absent', planned_minutes: 450, net_worked_minutes: 0 };
  if (opts.dayoff)  return base;

  // Defaults for a regular work day: 08:00 → 16:00, 12:00–12:30 break
  const ci = opts.check_in  || '08:02';
  const co = opts.check_out || '16:05';
  const breakStart = '12:00';
  const breakEnd   = '12:30';
  const toMin = (s) => +s.slice(0,2) * 60 + +s.slice(3,5);
  const span  = toMin(co) - toMin(ci);
  const breakMin = 30;
  const net   = Math.max(0, span - breakMin);
  const schedEndMin = toMin(SAMPLE_SCHED_END);
  const ot   = Math.max(0, toMin(co) - schedEndMin);
  const late = Math.max(0, toMin(ci) - toMin(SAMPLE_SCHED_START));

  return {
    ...base,
    is_work_day: opts.weekendWork ? false : true,
    check_in:  iso(date, ci),
    check_out: iso(date, co),
    breaks: [{ start: iso(date, breakStart), end: iso(date, breakEnd) }],
    planned_minutes:    opts.weekendWork ? 0 : 450,
    net_worked_minutes: net,
    overtime_minutes:   opts.weekendWork ? 0 : ot,
    break_minutes:      breakMin,
    late_minutes:       late,
    is_adjusted:        !!opts.adjusted,
    adj_reason:         opts.adjusted ? 'Korrigjim shembull' : undefined,
  };
}

const SAMPLE_ROWS = [
  // Week 1
  sampleRow('2026-03-02'),
  sampleRow('2026-03-03', { check_out: '16:20' }),               // light OT
  sampleRow('2026-03-04'),
  sampleRow('2026-03-05', { check_in: '08:18' }),                // late
  sampleRow('2026-03-06', { check_out: '17:00' }),               // 1h OT
  sampleRow('2026-03-07', { dayoff: true }),
  sampleRow('2026-03-08', { dayoff: true }),
  // Week 2
  sampleRow('2026-03-09', { absent: true }),                     // absent
  sampleRow('2026-03-10'),
  sampleRow('2026-03-11'),
  sampleRow('2026-03-12', { adjusted: true, check_out: '16:15' }),
  sampleRow('2026-03-13'),
  sampleRow('2026-03-14', { weekendWork: true, check_in: '09:00', check_out: '13:00' }),
  sampleRow('2026-03-15', { dayoff: true }),
];

const SAMPLE_WORKER = {
  schedule_start: SAMPLE_SCHED_START,
  schedule_end:   SAMPLE_SCHED_END,
};

// ── Compact Summary Strip ─────────────────────────────────────────────────────

/** E hëna e javës së datës (YYYY-MM-DD) — javët e raportit nisin të hënën. */
function mondayOf(ds) {
  const d = new Date(`${ds}T12:00:00`);
  const dow = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - dow);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const dm = (ds) => `${ds.slice(8, 10)}.${ds.slice(5, 7)}`;

/**
 * Orët sipas llojit të punës — muaji dhe java.
 *
 * Kërkesa e klientit: çdo agjent i sheh orët e veta sipas tipit (produktive,
 * admin, orë shtesë të akumuluara…) edhe javë për javë, p.sh. «40 orë
 * produktive». Burimi është `type_minutes` i çdo dite (backend: segmentet e
 * orarit + pjesa tjetër te tipi i ditës), jo një ngjyrë për tërë ditën.
 */
function useTypeBreakdown(rows, worker) {
  return useMemo(() => {
    const meta = new Map();
    for (const wt of (worker?.working_types || [])) meta.set(wt.code, { name: wt.name, color: wt.color });
    for (const r of rows) {
      if (r.working_type_code && !meta.has(r.working_type_code)) {
        meta.set(r.working_type_code, { name: r.working_type_name, color: r.working_type_color });
      }
    }
    const month = new Map();   // code → { planned, worked }
    const weeks = new Map();   // monday → { from, to, byCode: Map(code → {planned, worked}), planned, worked }
    for (const r of rows) {
      const tm = r.type_minutes;
      if (!tm) continue;
      const wk = mondayOf(r.date);
      const w = weeks.get(wk) || { from: r.date, to: r.date, byCode: new Map(), planned: 0, worked: 0 };
      if (r.date < w.from) w.from = r.date;
      if (r.date > w.to) w.to = r.date;
      for (const [kind, obj] of [['planned', tm.planned], ['worked', tm.worked]]) {
        for (const [code, mins] of Object.entries(obj || {})) {
          const m = month.get(code) || { planned: 0, worked: 0 };
          m[kind] += mins; month.set(code, m);
          const c = w.byCode.get(code) || { planned: 0, worked: 0 };
          c[kind] += mins; w.byCode.set(code, c);
          w[kind] += mins;
        }
      }
      weeks.set(wk, w);
    }
    const types = [...month.entries()]
      .map(([code, v]) => ({ code, ...v, name: meta.get(code)?.name || code, color: meta.get(code)?.color || '#64748B' }))
      .sort((a, b) => (b.planned + b.worked) - (a.planned + a.worked));
    return { types, weeks: [...weeks.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, v]) => v) };
  }, [rows, worker]);
}

function SummaryBar({ rows, worker, year, month }) {
  const { t } = useTranslation();
  const kpi = useMemo(() => {
    const wd      = rows.filter(r => r.is_work_day);
    const weekend = rows.filter(r => !r.is_work_day && r.check_in);
    const planned = wd.reduce((s, r) => s + (Number(r.planned_minutes)    || 0), 0);
    const worked  = wd.reduce((s, r) => s + (Number(r.net_worked_minutes) || 0), 0);
    const overtime = wd.reduce((s, r) => s + (Number(r.overtime_minutes) || 0), 0);
    const breaks   = wd.reduce((s, r) => s + (Number(r.break_minutes)    || 0), 0);
    const weekendMin = weekend.reduce((s, r) => s + (Number(r.net_worked_minutes) || 0), 0);
    const night    = rows.reduce((s, r) => s + nightMinutes(r), 0);
    return {
      planned, worked, diff: worked - planned, overtime, breaks,
      late:     wd.reduce((s, r) => s + (Number(r.late_minutes)     || 0), 0),
      absent:   wd.filter(r => r.compliance_status === 'absent').length,
      workDays: wd.length,
      adjusted: rows.filter(r => r.is_adjusted).length,
      weekend:  weekendMin,
      night,
    };
  }, [rows]);
  const { types, weeks } = useTypeBreakdown(rows, worker);

  const schedStr = worker?.schedule_start && worker?.schedule_end
    ? `${String(worker.schedule_start).slice(0, 5)}–${String(worker.schedule_end).slice(0, 5)}`
    : null;
  const pct = (a, b) => (b > 0 ? Math.round((a / b) * 1000) / 10 : null);
  const donePct = pct(kpi.worked, kpi.planned);

  // Kryesoret: plani kundrejt realitetit. Të tjerat — të vogla, poshtë.
  const primary = [
    { label: 'Planifikuar',  value: fmtHM(kpi.planned), color: '#1a73e8' },
    { label: 'Punuar',       value: fmtHM(kpi.worked),  color: '#1e8e3e', sub: donePct != null ? `${donePct}% e planit` : null },
    { label: 'Diferenca',    value: fmtDiff(kpi.diff),  color: kpi.diff >= 0 ? '#1e8e3e' : '#d93025' },
    { label: 'Pauza',        value: fmtHM(kpi.breaks),  color: '#b36200' },
    { label: 'Mungesa',      value: `${kpi.absent} ditë`, color: kpi.absent > 0 ? '#d93025' : '#999',
      sub: kpi.workDays ? `${pct(kpi.absent, kpi.workDays) ?? 0}% e ditëve` : null },
  ];
  const secondary = [
    { label: 'Mbi orar', value: fmtHM(kpi.overtime), on: kpi.overtime > 0 },
    { label: 'Fundjavë', value: fmtHM(kpi.weekend),  on: kpi.weekend > 0 },
    { label: t('report.nightWork'), value: fmtHM(kpi.night), on: kpi.night > 0 },
    { label: t('report.lateLabel'), value: `${kpi.late} min`, on: kpi.late > 0 },
    { label: 'Korrigjime', value: String(kpi.adjusted), on: kpi.adjusted > 0 },
  ];

  const projList = Array.isArray(worker?.projects) ? worker.projects : [];
  const cellSx = { fontSize: 11.5, py: 0.5, px: 1, whiteSpace: 'nowrap', borderColor: 'divider' };

  return (
    <Box sx={{ mb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{
        display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch',
        border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper',
      }}>
        {/* Identiteti */}
        <Box sx={{
          background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)', color: 'white',
          px: 2, py: 1.25, display: 'flex', flexDirection: 'column', justifyContent: 'center',
          minWidth: { xs: 'auto', md: 220 }, flexShrink: 0,
        }}>
          <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.8, opacity: 0.85 }}>
            {t('report.monthlyScheduleTitle').toUpperCase()}
          </Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25 }}>{workerFullName(worker) || '—'}</Typography>
          <Typography sx={{ fontSize: 11, opacity: 0.85, mt: 0.25 }}>
            {t('report.month_' + month)} {year}{schedStr ? ` · ${schedStr}` : ''}
          </Typography>
          {worker?.schedule_name && <Typography sx={{ fontSize: 10, opacity: 0.7 }}>{worker.schedule_name}</Typography>}
          {worker?.supervisor_name && (
            <Typography sx={{ fontSize: 10, opacity: 0.8, mt: 0.25 }}>{t('report.supervisor')}: {worker.supervisor_name}</Typography>
          )}
        </Box>

        {/* KPI */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(5, 1fr)' }, flex: 1 }}>
            {primary.map((k) => (
              <Box key={k.label} sx={{ px: 1.5, py: 1, borderLeft: { sm: '1px solid' }, borderBottom: { xs: '1px solid', sm: 'none' }, borderColor: 'divider' }}>
                <Typography sx={{ fontSize: 10.5, color: 'text.secondary', fontWeight: 600 }}>{k.label}</Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: k.color, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>{k.value}</Typography>
                {k.sub && <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{k.sub}</Typography>}
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, px: 1.5, py: 0.6, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
            {secondary.map((k) => (
              <Typography key={k.label} sx={{ fontSize: 11, color: k.on ? 'text.primary' : 'text.disabled' }}>
                {k.label}: <b style={{ fontVariantNumeric: 'tabular-nums' }}>{k.value}</b>
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Sipas llojit të punës — emrat e fjalorit të kompanisë */}
      {types.length > 0 && (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper' }}>
          <Box sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'baseline', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>Orët sipas llojit të punës</Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>punuar / planifikuar</Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(auto-fill, minmax(150px, 1fr))', md: 'repeat(auto-fill, minmax(185px, 1fr))' }, gap: 1, p: 1 }}>
            {types.map((tp) => {
              const p = tp.planned > 0 ? Math.min(100, (tp.worked / tp.planned) * 100) : (tp.worked > 0 ? 100 : 0);
              return (
                <Box key={tp.code} sx={{ p: 1, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', borderLeft: `4px solid ${tp.color}` }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={tp.name}>
                    {tp.name}
                  </Typography>
                  <Typography sx={{ fontSize: 17, fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1.25 }}>
                    {fmtHM(tp.worked)}
                    <Typography component="span" sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 600 }}> / {fmtHM(tp.planned)}</Typography>
                  </Typography>
                  <Box sx={{ height: 4, borderRadius: 2, bgcolor: 'action.hover', mt: 0.5, overflow: 'hidden' }}>
                    <Box sx={{ width: `${p}%`, height: '100%', bgcolor: tp.color }} />
                  </Box>
                </Box>
              );
            })}
          </Box>

          {weeks.length > 1 && (
            <Box sx={{ overflowX: 'auto', borderTop: '1px solid', borderColor: 'divider' }}>
              <Table size="small" sx={{ minWidth: 480 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...cellSx, fontWeight: 800 }}>Java</TableCell>
                    {types.map((tp) => (
                      <TableCell key={tp.code} align="right" sx={{ ...cellSx, fontWeight: 700 }}>
                        <Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '2px', bgcolor: tp.color, mr: 0.5 }} />
                        {tp.name}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ ...cellSx, fontWeight: 800 }}>Gjithsej</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {weeks.map((w) => (
                    <TableRow key={w.from} hover>
                      <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{dm(w.from)} – {dm(w.to)}</TableCell>
                      {types.map((tp) => {
                        const c = w.byCode.get(tp.code);
                        return (
                          <TableCell key={tp.code} align="right" sx={{ ...cellSx, fontVariantNumeric: 'tabular-nums' }}>
                            {c ? <><b>{fmtHM(c.worked)}</b><Box component="span" sx={{ color: 'text.secondary' }}> / {fmtHM(c.planned)}</Box></> : <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>}
                          </TableCell>
                        );
                      })}
                      <TableCell align="right" sx={{ ...cellSx, fontVariantNumeric: 'tabular-nums' }}>
                        <b>{fmtHM(w.worked)}</b><Box component="span" sx={{ color: 'text.secondary' }}> / {fmtHM(w.planned)}</Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      )}

      {/* Projects planned this month (from worker_project_assignments) */}
      {projList.length > 0 && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap',
          px: 1.25, py: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'action.hover',
        }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.secondary', mr: 0.25 }}>{t('report.projects')}:</Typography>
          {projList.map(p => (
            <Chip key={p.id} size="small" label={`${p.name} · ${fmtHM((Number(p.hours) || 0) * 60)}`}
              sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: '#1565c0', color: '#fff', '& .MuiChip-label': { px: 0.75 } }} />
          ))}
        </Box>
      )}
    </Box>
  );
}

// ── Compact Legend ────────────────────────────────────────────────────────────

function MatrixLegend() {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap',
        px: 1.5, py: 0.5, borderBottom: '1px solid', borderColor: 'divider',
        bgcolor: 'action.hover',
      }}
    >
      {[
        { bg: '#000000', label: t('report.workDay') },
        { bg: '#6a1b9a', label: 'OT' },
        { bg: '#e65100', label: t('report.breakCol') },
      ].map(item => (
        <Stack key={item.label} direction="row" spacing={0.4} alignItems="center">
          <Box sx={{ width: 10, height: 10, bgcolor: item.bg, borderRadius: '2px' }} />
          <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>{item.label}</Typography>
        </Stack>
      ))}
      {/* Planned-but-not-worked band — strong blue hatch (matches cell rendering) */}
      <Stack direction="row" spacing={0.4} alignItems="center">
        <Box sx={{ width: 10, height: 10, borderRadius: '2px',
          backgroundImage: 'repeating-linear-gradient(135deg, rgba(21,101,192,0.85), rgba(21,101,192,0.85) 3px, rgba(13,71,161,1) 3px, rgba(13,71,161,1) 6px)' }} />
        <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>{t('report.planned')}</Typography>
      </Stack>
      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#d93025' }}>A={t('report.absentLabel')}</Typography>
      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#aaa' }}>—={t('report.dayOff')}</Typography>
      <Box sx={{ width: 10, height: 10, bgcolor: 'rgba(211,47,47,0.45)', borderRadius: '2px',
        backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 2px, rgba(255,255,255,0.25) 2px, rgba(255,255,255,0.25) 4px)' }} />
      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#d93025' }}>✕ Hequr</Typography>
      {/* Ikonat e veprimeve mbi rresht ranë bashkë me veprimet: ky ekran është
          vetëm shikim, dhe një legjendë që shpjegon një buton që s'ekziston
          është premtim i rremë. Dita e korrigjuar dallohet nga vetë ngjyra. */}
      <Box sx={{ width: '1px', height: 14, bgcolor: 'divider', mx: 0.5 }} />
      <Stack direction="row" spacing={0.4} alignItems="center">
        <VisibilityOutlinedIcon sx={{ fontSize: 14, color: '#90a4ae' }} />
        <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>{t('report.readOnlyView', 'Vetëm shikim')}</Typography>
      </Stack>
    </Box>
  );
}

// ── SlotEditDialog u hoq bashkë me editimin ─────────────────────────────────
// Ky ekran është vetëm shikim. Korrigjimi i një slot-i 10-minutësh vazhdon të
// jetojë te rruga e vet (`/api/attendance/slot-edits`) dhe te Qendra e
// Aprovimeve; këtu slot-et e hequra vetëm SHFAQEN, nuk preken.

// ── All Workers Horizontal Matrix (FlixBus-style) ────────────────────────────

const TODAY_STR = new Date().toISOString().slice(0, 10);

function AllWorkersMatrix({ data, days, weeks, loading, onSelect, onDayClick, fDept }) {
  const { t } = useTranslation();

  const filtered = useMemo(() => {
    const base = !fDept ? (data || []) : (data || []).filter(w => String(w.department_id) === String(fDept));
    // Group workers by department so each dept renders as one contiguous block
    // (separator rows assume grouping). Departments keep first-appearance order,
    // workers keep their original order within a department — stable sort.
    const deptOrder = new Map();
    base.forEach(w => {
      if (!deptOrder.has(w.department_id)) deptOrder.set(w.department_id, deptOrder.size);
    });
    return base
      .map((w, i) => [w, i])
      .sort((a, b) => {
        const da = deptOrder.get(a[0].department_id);
        const db = deptOrder.get(b[0].department_id);
        return da !== db ? da - db : a[1] - b[1];
      })
      .map(([w]) => w);
  }, [data, fDept]);

  // ── Per-day team totals ───────────────────────────────────────────────────
  const teamDay = useMemo(() => {
    const m = {};
    for (const d of (days || [])) {
      let present = 0, absent = 0, worked = 0;
      for (const w of filtered) {
        const day = w.days?.[d.date];
        if (!day || !day.is_work_day) continue;
        if (day.status === 'absent') absent++;
        else if (day.status !== 'scheduled') { present++; worked += Number(day.net_worked_minutes || 0); }
      }
      m[d.date] = { present, absent, worked };
    }
    return m;
  }, [days, filtered]);

  // Unique department groups for group separator rows
  const deptGroups = useMemo(() => {
    const seen = new Set(), order = [];
    for (const w of filtered) {
      if (!seen.has(w.department_id)) {
        seen.add(w.department_id);
        order.push({ id: w.department_id, name: w.department_name || '—' });
      }
    }
    return order;
  }, [filtered]);

  // Worker to dept index
  const workerDept = useMemo(() => {
    const m = {};
    for (const w of filtered) m[w.worker_id] = w.department_id;
    return m;
  }, [filtered]);

  if (loading) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={28} thickness={4} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Duke ngarkuar matricën…</Typography>
      </Box>
    );
  }
  if (!days || days.length === 0) return null;

  // ── Layout constants ──────────────────────────────────────────────────────
  const NAME_W = 162;
  const DAY_W  = 46;   // wider to fit HH:MM times
  const WK_W   = 56;
  const TOT_W  = 60;
  const ROW_H  = 38;   // taller for two-line in/out display
  const HDR_H  = 36;

  // ── Colour palette (matches FlixBus palette) ──────────────────────────────
  const C = {
    hdrBg:      '#3a4f6b',   // softer navy
    hdrFg:      '#f0f4f8',
    weekHdrBg:  '#4a6080',   // muted slate
    totHdrBg:   '#2d3f55',
    nameBg:     '#f7f9fb',
    nameHover:  '#edf1f7',
    dayWork:    '#ddeeff',   // very soft blue — worked normally
    dayWorkFg:  '#2a5298',
    dayLate:    '#fff4e0',   // very soft amber — arrived late
    dayLateFg:  '#a05c00',
    dayOT:      '#f0e8f8',   // very soft lavender — overtime
    dayOTFg:    '#6b3fa0',
    dayAbsent:  '#fde8e8',   // very soft rose — absent
    dayAbsFg:   '#a03030',
    daySched:   '#eef4fb',   // pale blue — scheduled (future) day, not yet worked
    daySchedFg: '#8aa4c0',
    dayOff:     '#f4f5f6',   // near-white grey — weekend/off
    dayOffFg:   '#aab4be',
    todayBdr:   '#4a7fc1',
    todayHdr:   '#4a7fc1',
    wkBg:       '#f0f2f9',   // very soft indigo tint
    wkFg:       '#3d5299',
    wkBad:      '#fdf0f3',
    wkBadFg:    '#a03060',
    totBg:      '#edf7f0',   // very soft green
    totFg:      '#2e6e44',
    totBad:     '#fdf1f1',
    totBadFg:   '#a03030',
    teamBg:     '#eaf4fd',
    teamFg:     '#2a5298',
    border:     '#dde3ea',
    borderWk:   '#9bafd0',
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const fmtH = min => {
    if (!min) return '';
    const h = Math.floor(min / 60), mn = min % 60;
    return mn === 0 ? `${h}` : `${h}:${String(mn).padStart(2,'0')}`;
  };
  const fmtHFull = min => {
    if (!min && min !== 0) return '—';
    const h = Math.floor(min / 60), mn = min % 60;
    return `${h}:${String(mn).padStart(2,'0')}`;
  };

  // Total column count for colSpan
  const totalCols = 1 + days.length + weeks.length + 1; // name | days+wkTotals | monthTotal

  const cellSt = (extra = {}) => ({
    border: `1px solid ${C.border}`,
    height: ROW_H,
    verticalAlign: 'middle',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    padding: 0,
    boxSizing: 'border-box',
    ...extra,
  });

  return (
    <Box sx={{ overflowX: 'auto', fontFamily: '"Roboto Mono","Courier New",monospace', fontSize: 11 }}>
      <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>

        {/* ── COLGROUP — fixes widths ── */}
        <colgroup>
          <col style={{ width: NAME_W }} />
          {weeks.map(wk => (
            <React.Fragment key={`cg-${wk.iso_week}`}>
              {wk.dates.map(date => <col key={date} style={{ width: DAY_W }} />)}
              <col style={{ width: WK_W }} />
            </React.Fragment>
          ))}
          <col style={{ width: TOT_W }} />
        </colgroup>

        <thead>
          {/* ── ROW 1: Week group headers ── */}
          <tr style={{ height: 22 }}>
            <th style={{
              position: 'sticky', left: 0, zIndex: 5,
              background: C.hdrBg, color: C.hdrFg,
              fontSize: 10, fontWeight: 700, textAlign: 'left',
              padding: '0 8px', border: `1px solid ${C.weekHdrBg}`,
            }}>
              {t('report.worker')}
            </th>
            {weeks.map(wk => (
              <th
                key={wk.iso_week}
                colSpan={wk.dates.length + 1}
                style={{
                  background: C.weekHdrBg, color: C.hdrFg,
                  fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                  border: `1px solid ${C.hdrBg}`,
                  borderLeft: `2px solid ${C.borderWk}`,
                  padding: '2px 0',
                }}
              >
                KW {String(wk.iso_week).padStart(2, '0')}
              </th>
            ))}
            <th style={{
              background: C.totHdrBg, color: '#fff',
              fontSize: 10, fontWeight: 700,
              border: `1px solid ${C.hdrBg}`,
              borderLeft: `2px solid #5c6bc0`,
              padding: '2px 0',
            }}>
              Total
            </th>
          </tr>

          {/* ── ROW 2: Day number + short day name ── */}
          <tr style={{ height: HDR_H }}>
            <th style={{
              position: 'sticky', left: 0, zIndex: 5,
              background: C.hdrBg, color: '#aac4e0',
              fontSize: 9, textAlign: 'left', padding: '0 8px',
              border: `1px solid ${C.weekHdrBg}`,
            }}>
              {days.length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>
                    {t(`report.month_${Number(days[0].date.slice(5,7))}`)} {days[0].date.slice(0,4)}
                  </div>
                  <div style={{ fontSize: 9, color: '#7faacc', marginTop: 1 }}>
                    {filtered.length} {t('report.worker').toLowerCase()}
                  </div>
                </>
              )}
            </th>
            {weeks.map(wk => (
              <React.Fragment key={`hdr2-${wk.iso_week}`}>
                {wk.dates.map(date => {
                  const d = days.find(x => x.date === date);
                  if (!d) return <th key={date} />;
                  const isToday = date === TODAY_STR;
                  const isWE = d.is_weekend;
                  return (
                    <th
                      key={date}
                      style={{
                        background: isToday ? C.todayHdr : isWE ? '#1e2d42' : C.hdrBg,
                        color: isToday ? '#fff' : isWE ? '#607d8b' : '#aac4e0',
                        fontSize: 9, fontWeight: isToday ? 800 : 600,
                        border: `1px solid ${C.weekHdrBg}`,
                        borderLeft: isToday ? `2px solid ${C.todayBdr}` : undefined,
                        borderRight: isToday ? `2px solid ${C.todayBdr}` : undefined,
                        verticalAlign: 'middle', padding: '2px 0',
                        outline: isToday ? `2px solid ${C.todayBdr}` : 'none',
                        outlineOffset: -1,
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 700, color: isToday ? '#fff' : isWE ? '#546e7a' : '#cce0f5', lineHeight: 1.2 }}>
                        {d.day_num}
                      </div>
                      <div style={{ fontSize: 8, lineHeight: 1 }}>
                        {d.day_short}
                      </div>
                    </th>
                  );
                })}
                {/* Week-total sub-header */}
                <th style={{
                  background: '#1d2f45', color: '#7faacc',
                  fontSize: 8, fontWeight: 700,
                  border: `1px solid ${C.hdrBg}`,
                  borderLeft: `2px solid ${C.borderWk}`,
                  borderRight: `2px solid ${C.borderWk}`,
                  verticalAlign: 'middle',
                }}>
                  <div>Pun</div><div style={{ color: '#4a7aa0', fontWeight: 400 }}>Pl</div>
                </th>
              </React.Fragment>
            ))}
            {/* Monthly total col header */}
            <th style={{
              background: C.totHdrBg, color: '#7faacc',
              fontSize: 8, fontWeight: 700,
              border: `1px solid ${C.hdrBg}`,
              borderLeft: `2px solid #5c6bc0`,
              verticalAlign: 'middle',
            }}>
              <div>Pun</div><div style={{ color: '#4a7aa0', fontWeight: 400 }}>Pl&nbsp;/&nbsp;A</div>
            </th>
          </tr>

          {/* ── ROW 3: Team totals per day ── */}
          <tr style={{ height: 22, background: C.teamBg }}>
            <td style={{
              position: 'sticky', left: 0, zIndex: 4,
              background: C.teamBg, color: C.teamFg,
              fontSize: 9, fontWeight: 800, textAlign: 'left',
              padding: '0 8px', border: `1px solid #bbcfe0`,
            }}>
              Team ({filtered.length})
            </td>
            {weeks.map(wk => (
              <React.Fragment key={`team-${wk.iso_week}`}>
                {wk.dates.map(date => {
                  const d = days.find(x => x.date === date);
                  const td = teamDay[date] || { present: 0, absent: 0 };
                  const isToday = date === TODAY_STR;
                  const isWE = d?.is_weekend;
                  return (
                    <td key={date} style={{
                      background: isToday ? '#e3f2fd' : isWE ? '#f0f4f8' : C.teamBg,
                      border: `1px solid #bbcfe0`,
                      borderLeft: isToday ? `2px solid ${C.todayBdr}` : undefined,
                      borderRight: isToday ? `2px solid ${C.todayBdr}` : undefined,
                      fontSize: 9, textAlign: 'center', verticalAlign: 'middle', padding: 0,
                    }}>
                      {!isWE && (
                        <div>
                          {td.present > 0 && <span style={{ color: '#1565c0', fontWeight: 700 }}>{td.present}</span>}
                          {td.absent > 0  && <span style={{ color: '#c62828', fontWeight: 700 }}>{td.absent > 0 ? ` -${td.absent}` : ''}</span>}
                        </div>
                      )}
                    </td>
                  );
                })}
                <td style={{
                  background: '#dce9f8', fontSize: 9, fontWeight: 700, color: C.teamFg,
                  border: `1px solid #bbcfe0`,
                  borderLeft: `2px solid ${C.borderWk}`, borderRight: `2px solid ${C.borderWk}`,
                  textAlign: 'center', verticalAlign: 'middle',
                }}>
                  {(() => {
                    const wkWorked = weeks.find(w => w.iso_week === wk.iso_week)?.dates
                      .reduce((s, dt) => s + (teamDay[dt]?.worked || 0), 0) || 0;
                    return fmtHFull(wkWorked);
                  })()}
                </td>
              </React.Fragment>
            ))}
            <td style={{
              background: '#c8e6c9', fontSize: 9, fontWeight: 700, color: '#1b5e20',
              border: `1px solid #bbcfe0`, borderLeft: `2px solid #5c6bc0`,
              textAlign: 'center', verticalAlign: 'middle',
            }}>
              {fmtHFull(Object.values(teamDay).reduce((s, d) => s + (d.worked || 0), 0))}
            </td>
          </tr>
        </thead>

        {/* ── BODY ── */}
        <tbody>
          {filtered.length === 0 && (
            <tr>
              <td colSpan={totalCols} style={{ textAlign: 'center', padding: '32px', color: '#888', fontSize: 12 }}>
                {t('report.noDataForPeriod')}
              </td>
            </tr>
          )}

          {(() => {
            const rows = [];
            let lastDept = null;
            filtered.forEach((w, wi) => {
              // Department separator row
              if (w.department_id !== lastDept) {
                lastDept = w.department_id;
                rows.push(
                  <tr key={`dept-${w.department_id}-${wi}`} style={{ height: 18 }}>
                    <td
                      colSpan={totalCols}
                      style={{
                        background: '#37474f', color: '#cfd8dc',
                        fontSize: 9, fontWeight: 700, letterSpacing: 0.8,
                        padding: '0 10px', border: `1px solid #455a64`,
                        textTransform: 'uppercase',
                      }}
                    >
                      {w.department_name || '—'}
                    </td>
                  </tr>
                );
              }

              const diff = Number(w.diff_minutes || 0);
              const rowTotBg = diff >= 0 ? C.totBg   : C.totBad;
              const rowTotFg = diff >= 0 ? C.totFg   : C.totBadFg;

              rows.push(
                <tr
                  key={w.worker_id}
                  style={{ height: ROW_H, cursor: 'pointer' }}
                  onClick={() => onSelect(w.worker_id)}
                  onMouseEnter={e => e.currentTarget.style.filter = 'brightness(0.93)'}
                  onMouseLeave={e => e.currentTarget.style.filter = ''}
                >
                  {/* Name — sticky */}
                  <td style={{
                    position: 'sticky', left: 0, zIndex: 2,
                    background: '#f8fafc', border: `1px solid ${C.border}`,
                    padding: '0 8px', fontSize: 11, fontWeight: 600, color: '#1a2b47',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    maxWidth: NAME_W,
                  }}>
                    <span style={{ fontSize: 9, color: '#90a4ae', marginRight: 4 }}>
                      {w.schedule_start ? String(w.schedule_start).slice(0,5) : ''}
                    </span>
                    {`${w.first_name || ''} ${w.last_name || ''}`.trim()}
                  </td>

                  {/* Days — per week group, then weekly total */}
                  {weeks.map(wk => (
                    <React.Fragment key={wk.iso_week}>
                      {wk.dates.map(date => {
                        const day   = w.days?.[date];
                        const dInfo = days.find(x => x.date === date);
                        const isToday  = date === TODAY_STR;
                        const isWE     = dInfo?.is_weekend;

                        if (!day) {
                          return <td key={date} style={cellSt({ background: '#fafafa' })} />;
                        }

                        let bg, fg, label, title;
                        if (!day.is_work_day) {
                          bg = C.dayOff; fg = C.dayOffFg; label = ''; title = 'Pushim';
                        } else if (day.status === 'scheduled') {
                          bg = C.daySched; fg = C.daySchedFg; label = ''; title = 'E planifikuar';
                        } else if (day.status === 'absent') {
                          bg = C.dayAbsent; fg = C.dayAbsFg; label = 'A'; title = 'Mungon';
                        } else if (day.late_minutes > 0) {
                          bg = C.dayLate; fg = C.dayLateFg;
                          label = fmtH(day.net_worked_minutes);
                          title = `Vonë ${day.late_minutes}m · ${fmtHFull(day.net_worked_minutes)}`;
                        } else {
                          bg = C.dayWork; fg = C.dayWorkFg;
                          label = fmtH(day.net_worked_minutes);
                          title = fmtHFull(day.net_worked_minutes);
                        }

                        const ciStr = day.check_in  || null;
                        const coStr = day.check_out || null;

                        return (
                          <td
                            key={date}
                            onClick={(e) => { e.stopPropagation(); if (onDayClick) onDayClick(w, date, day); }}
                            style={cellSt({
                              background: bg, color: fg,
                              fontWeight: day.status === 'absent' ? 800 : 500,
                              fontSize: 9,
                              borderLeft:  isToday ? `2px solid ${C.todayBdr}` : undefined,
                              borderRight: isToday ? `2px solid ${C.todayBdr}` : undefined,
                              position: 'relative',
                              verticalAlign: 'middle',
                              lineHeight: 1.25,
                              cursor: 'pointer',
                            })}
                          >
                            {/* Late indicator — top-right triangle */}
                            {day.late_minutes > 0 && (
                              <span style={{
                                position: 'absolute', top: 0, right: 0,
                                width: 0, height: 0,
                                borderStyle: 'solid',
                                borderWidth: '0 7px 7px 0',
                                borderColor: `transparent ${C.dayLateFg} transparent transparent`,
                              }} />
                            )}
                            {/* Show in/out times for working days, letter for absent, empty for day-off */}
                            {day.status === 'absent' ? (
                              <span style={{ fontSize: 11, fontWeight: 800 }}>A</span>
                            ) : day.is_work_day && ciStr ? (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
                                <span style={{ fontSize: 8, color: '#2e6e44', fontWeight: 600, letterSpacing: -0.3 }}>{ciStr}</span>
                                <span style={{ fontSize: 8, color: '#7a3030', fontWeight: 600, letterSpacing: -0.3 }}>{coStr || '--:--'}</span>
                              </div>
                            ) : (
                              label
                            )}
                          </td>
                        );
                      })}

                      {/* Weekly total */}
                      {(() => {
                        const wt   = w.weekly_totals?.[wk.iso_week] || { worked: 0, planned: 0 };
                        const wDff = wt.worked - wt.planned;
                        const wBg  = wt.planned === 0 ? '#f5f5f5' : wDff >= 0 ? C.wkBg : C.wkBad;
                        const wFg  = wt.planned === 0 ? '#bbb'    : wDff >= 0 ? C.wkFg : C.wkBadFg;
                        return (
                          <td style={cellSt({
                            background: wBg, color: wFg,
                            fontWeight: 700, fontSize: 9,
                            borderLeft:  `2px solid ${C.borderWk}`,
                            borderRight: `2px solid ${C.borderWk}`,
                          })}>
                            <div>{fmtHFull(wt.worked)}</div>
                            {wt.planned > 0 && (
                              <div style={{ fontSize: 7.5, color: '#8090b0', fontWeight: 400 }}>
                                {fmtHFull(wt.planned)}
                              </div>
                            )}
                          </td>
                        );
                      })()}
                    </React.Fragment>
                  ))}

                  {/* Monthly total */}
                  <td style={cellSt({
                    background: rowTotBg, color: rowTotFg,
                    fontWeight: 700, fontSize: 9,
                    borderLeft: `2px solid #5c6bc0`,
                  })}>
                    <div style={{ fontWeight: 800 }}>{fmtHFull(w.total_worked)}</div>
                    <div style={{ fontSize: 7.5, color: '#7986cb', fontWeight: 400 }}>{fmtHFull(w.total_planned)}</div>
                    {w.total_absent > 0 && (
                      <div style={{ fontSize: 7.5, color: C.totBadFg, fontWeight: 700 }}>
                        {w.total_absent}A
                      </div>
                    )}
                  </td>
                </tr>
              );
            });
            return rows;
          })()}
        </tbody>
      </table>

      {/* ── Legend ── */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', px: 1.5, py: 0.75, borderTop: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
        {[
          { bg: C.dayWork,   fg: C.dayWorkFg,  label: 'Punoi' },
          { bg: C.dayLate,   fg: C.dayLateFg,  label: 'Vonë' },
          { bg: C.dayAbsent, fg: C.dayAbsFg,   label: 'Mungon (A)' },
          { bg: C.daySched,  fg: C.daySchedFg, label: 'E planifikuar' },
          { bg: C.dayOff,    fg: C.dayOffFg,   label: 'Pushim' },
        ].map(item => (
          <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: item.bg, border: `1px solid ${item.fg}`, borderRadius: '2px' }} />
            <Typography sx={{ fontSize: 9, color: item.fg, fontWeight: 600 }}>{item.label}</Typography>
          </Box>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ position: 'relative', width: 12, height: 12, bgcolor: C.dayLate, border: `1px solid ${C.dayLateFg}`, borderRadius: '2px', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', top: 0, right: 0, width: 0, height: 0, borderStyle: 'solid', borderWidth: '0 7px 7px 0', borderColor: `transparent ${C.dayLateFg} transparent transparent` }} />
          </Box>
          <Typography sx={{ fontSize: 9, color: '#666' }}>▲ vonë</Typography>
        </Box>
      </Box>
    </Box>
  );
}


// ── Main Component ────────────────────────────────────────────────────────────

export default function MonthlyIndividualWorkScheduleReport() {
  const { t } = useTranslation();
  const { hasAnyRole } = usePermissions();
  const location = useLocation();
  const canViewOthers = useMemo(
    () => hasAnyRole(['super_admin', 'admin', 'company_admin', 'hr_manager', 'team_lead', 'menaxher', 'manager']),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  /**
   * Hyrja nga jashtë: `?worker=29&date=2026-09-10`.
   *
   * Plani i Punës e dërgonte tashmë këtë lidhje — dhe kjo faqe e shpërfillte,
   * ndaj klikimi mbi një emër të çonte te një raport bosh ku duhej ta zgjidhje
   * sërish të njëjtin njeri, dhe muajin bashkë me të. Parametrat lexohen te
   * gjendja FILLESTARE, jo te një efekt: lista e punëtorëve vjen më vonë, dhe
   * një vendosje pas saj do të shihej si dy pamje brenda një çasti.
   */
  const qs = new URLSearchParams(location.search);
  const qDate   = /^\d{4}-\d{2}-\d{2}$/.test(qs.get('date') || '') ? qs.get('date') : null;
  const qWorker = qs.get('worker') || '';

  const [year,       setYear]       = useState(qDate ? Number(qDate.slice(0, 4)) : THIS_YEAR);
  const [month,      setMonth]      = useState(qDate ? Number(qDate.slice(5, 7)) : THIS_MON);
  const [fDept,      setFDept]      = useState('');
  const [fProject,   setFProject]   = useState('');
  const [fWorker,    setFWorker]    = useState(qWorker);
  const [workers,    setWorkers]    = useState([]);
  const [depts,      setDepts]      = useState([]);
  const [projects,   setProjects]   = useState([]);
  const [rows,       setRows]       = useState([]);
  const [workerInfo, setWorkerInfo] = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [loaded,     setLoaded]     = useState(false);
  // slot-level edits: plain object keyed by "${date}:${slotMinute}"
  const [slotEdits,     setSlotEdits]     = useState({});
  // overview mode state
  // Pamja e parë është gjithmonë ajo individuale (kërkesë e klientit): një
  // punëtor, me orët e tij sipas tipit. Lista e të gjithëve mbetet një klikim
  // larg («Të gjithë»), por s'hapet më vetë.
  const [viewMode,      setViewMode]      = useState('individual');
  const [summaryData,   setSummaryData]   = useState([]);
  const [summaryDays,   setSummaryDays]   = useState([]);
  const [summaryWeeks,  setSummaryWeeks]  = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [dayDetailDlg, setDayDetailDlg] = useState(null); // { worker, date, day }
  // ── Period mode: daily | weekly | monthly ───────────────────────────────────
  const [periodMode, setPeriodMode] = useState('monthly');
  const [focusDate,  setFocusDate]  = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`;
  });

  // Nuk ka më `isAdmin` këtu: ekrani nuk ndryshon asgjë, ndaj s'ka çfarë të
  // gardhojë. `canViewOthers` mbetet — ai vendos vetëm KË sheh dikush, jo çfarë
  // preket, dhe ai dallim është pikërisht pika e View-t 2.
  const autoLoadDoneRef = useRef(false);

  // ── Load workers + departments on mount ──
  const loadFilters = useCallback(async () => {
    try {
      const [wR, dR, pR] = await Promise.all([
        fetch(`${API_URL}/api/workers`,     { headers: getAuthHeaders() }).then(r => r.json()).catch(() => ({})),
        fetch(`${API_URL}/api/departments`, { headers: getAuthHeaders() }).then(r => r.json()).catch(() => ({})),
        fetch(`${API_URL}/api/projects`,    { headers: getAuthHeaders() }).then(r => r.json()).catch(() => ({})),
      ]);
      setWorkers(wR?.data || []);
      setDepts(dR?.data   || []);
      setProjects(pR?.data || []);
    } catch (e) { console.error('[WorkScheduleReport] loadFilters:', e); }
  }, []);

  useEffect(() => { loadFilters(); }, [loadFilters]);

  // ── Load overview matrix (all workers, per-day) ──
  const loadSummary = useCallback(async (y, m, projectId) => {
    setSummaryLoading(true);
    try {
      const r = await fetch(
        `${API_URL}/api/reports/work-schedule-overview?year=${y}&month=${m}` +
          (projectId ? `&project_id=${projectId}` : ''),
        { headers: getAuthHeaders() }
      );
      const j = await r.json().catch(() => ({}));
      setSummaryData(j?.workers || []);
      setSummaryDays(j?.days    || []);
      setSummaryWeeks(j?.weeks  || []);
    } catch (e) {
      console.error('[WorkScheduleReport] loadSummary:', e);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  // ── Auto-load summary when in overview mode ──
  useEffect(() => {
    if (viewMode === 'overview' && canViewOthers) {
      loadSummary(year, month, fProject);
    }
  }, [viewMode, year, month, fProject, canViewOthers, loadSummary]);

  // ── Auto-select current user's worker record on first load ──
  // If ?worker=<id> is in the URL and the user can view others → pre-select that worker.
  // Non-privileged users always load their own worker regardless of the param.
  useEffect(() => {
    if (autoLoadDoneRef.current) return;

    // For non-admin users: ALWAYS load their own schedule, directly from the
    // session worker_id. This must NOT depend on the /api/workers list (an
    // employee may not be in it, or it may be empty/filtered) — the report data
    // is fetched per worker_id, and the worker name comes from the report itself.
    if (!canViewOthers) {
      const u = getCurrentUser() || {};
      const workerId = u.worker_id ?? u.WorkerId ?? u?.data?.worker_id ?? u?.data?.WorkerId ?? '';
      if (!workerId) return; // account not linked to a worker — nothing to load
      autoLoadDoneRef.current = true;
      setFWorker(String(workerId));
      setViewMode('individual');
      return;
    }

    // Admin/manager deep-link: ?worker=<id> — needs the workers list to validate.
    if (workers.length === 0) return;
    const params  = new URLSearchParams(location.search);
    const paramId = params.get('worker');
    if (paramId) {
      const match = workers.find(w => String(w.Id || w.id) === String(paramId));
      if (match) {
        autoLoadDoneRef.current = true;
        setFWorker(String(match.Id || match.id));
        setViewMode('individual');
        return;
      }
    }
    // Pa parametër: vetja (kur llogaria ka punëtor), ndryshe punëtori i parë
    // aktiv sipas emrit — faqja hapet me një orar, jo me një listë.
    const u = getCurrentUser() || {};
    const selfId = u.worker_id ?? u.WorkerId ?? u?.data?.worker_id ?? u?.data?.WorkerId ?? '';
    const active = workers.filter(w => w.Active !== false && w.active !== false);
    const self = selfId && active.find(w => String(w.Id || w.id) === String(selfId));
    const first = self || [...active].sort((a, b) =>
      `${a.FirstName || ''} ${a.LastName || ''}`.localeCompare(`${b.FirstName || ''} ${b.LastName || ''}`, 'sq'))[0];
    autoLoadDoneRef.current = true;
    if (first) setFWorker(String(first.Id || first.id));
    setViewMode('individual');
  }, [workers, location.search, canViewOthers]);

  // ── Load report data ──
  const loadData = useCallback(async (wid, y, m) => {
    if (!wid) return;
    setLoading(true); setLoaded(false); setRows([]); setWorkerInfo(null); setSlotEdits({});
    try {
      const [mainRes, slotRes] = await Promise.all([
        fetch(`${API_URL}/api/reports/monthly-work-schedule?worker_id=${wid}&year=${y}&month=${m}`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/attendance/slot-edits?worker_id=${wid}&year=${y}&month=${m}`, { headers: getAuthHeaders() }),
      ]);
      const j    = await mainRes.json().catch(() => ({}));
      const sj   = await slotRes.json().catch(() => ({}));
      setRows(j?.data || []);
      setWorkerInfo(j?.worker || null);
      // Build lookup map: "${adj_date}:${slot_minute}" → row
      const editMap = {};
      for (const se of (sj?.data || [])) {
        editMap[`${se.adj_date}:${se.slot_minute}`] = se;
      }
      setSlotEdits(editMap);
      setLoaded(true);
    } catch (e) {
      console.error('[WorkScheduleReport] load:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Auto-load on worker / year / month change ──
  useEffect(() => {
    if (fWorker) loadData(fWorker, year, month);
  }, [fWorker, year, month, loadData]);

  const handleReset = () => {
    autoLoadDoneRef.current = false;
    setFDept(''); setFProject(''); setFWorker('');
    setYear(THIS_YEAR); setMonth(THIS_MON);
    setRows([]); setWorkerInfo(null); setLoaded(false);
    if (canViewOthers) setViewMode('overview');
  };

  // Called when admin clicks a worker in the overview table
  const handleSelectWorker = wid => {
    setFWorker(String(wid));
    setViewMode('individual');
  };

  const backToOverview = () => {
    setFWorker('');
    setRows([]); setWorkerInfo(null); setLoaded(false);
    setViewMode('overview');
  };

  // ── Local date string helper (avoids UTC midnight off-by-one) ───────────
  const localDateStr = (d) => {
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  };

  // ── focusDate snapped to current year/month (1st, or today if same month) ─
  const snapFocusToMonth = useCallback((y, m) => {
    const today = localDateStr(new Date());
    const thisY = new Date().getFullYear();
    const thisM = new Date().getMonth() + 1;
    if (y === thisY && m === thisM) return today;
    return `${y}-${String(m).padStart(2,'0')}-01`;
  }, []);

  // Month navigation arrows
  const prevMonth = () => {
    let newY = year, newM = month - 1;
    if (newM < 1) { newY = year - 1; newM = 12; }
    setYear(newY); setMonth(newM);
    setFocusDate(snapFocusToMonth(newY, newM));
  };
  const nextMonth = () => {
    let newY = year, newM = month + 1;
    if (newM > 12) { newY = year + 1; newM = 1; }
    setYear(newY); setMonth(newM);
    setFocusDate(snapFocusToMonth(newY, newM));
  };

  // ── Daily / Weekly navigation (syncs month/year if crossing boundary) ────
  const shiftFocusDate = (days) => {
    const d = new Date(focusDate);
    d.setDate(d.getDate() + days);
    const s    = localDateStr(d);
    const newM = d.getMonth() + 1;
    const newY = d.getFullYear();
    setFocusDate(s);
    if (newM !== month || newY !== year) { setMonth(newM); setYear(newY); }
  };
  const prevDay  = () => shiftFocusDate(-1);
  const nextDay  = () => shiftFocusDate(+1);
  const prevWeek = () => shiftFocusDate(-7);
  const nextWeek = () => shiftFocusDate(+7);

  // ── Change period mode: snap focusDate to loaded month ───────────────────
  const handlePeriodMode = (v) => {
    if (!v) return;
    if (v !== 'monthly') setFocusDate(snapFocusToMonth(year, month));
    setPeriodMode(v);
  };

  const filteredWorkers = useMemo(() => {
    // Shih MonthlyWorkHoursReport: matet me periudhën, jo me flamurin «aktiv».
    let list = workers.filter(w => wasEmployedOn(w, `${year}-${String(month).padStart(2, '0')}-01`));
    if (fDept) {
      list = list.filter(w => String(w.department_id || w.Department || '') === String(fDept));
    }
    if (fProject) {
      list = list.filter(w => {
        const ids = w.ProjectIds || w.project_ids || [];
        return Array.isArray(ids) && ids.map(String).includes(String(fProject));
      });
    }
    return list;
  }, [workers, fDept, fProject, year, month]);

  // ── Week bounds for focusDate ──────────────────────────────────────────────────
  const weekBounds = useMemo(() => {
    const d = new Date(focusDate);
    if (isNaN(d)) return { start: focusDate, end: focusDate };
    const offset = d.getDay() === 0 ? 6 : d.getDay() - 1;
    const mon = new Date(d); mon.setDate(d.getDate() - offset);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return { start: mon.toISOString().slice(0, 10), end: sun.toISOString().slice(0, 10) };
  }, [focusDate]);

  // ISO week number helper
  const getISOWeekNum = (dateStr) => {
    const d = new Date(dateStr); d.setHours(0,0,0,0);
    d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
    const w1 = new Date(d.getFullYear(), 0, 4);
    return 1 + Math.round(((d.getTime() - w1.getTime()) / 86400000 - 3 + (w1.getDay() + 6) % 7) / 7);
  };

  // ── Rows filtered by active period mode ───────────────────────────────────────
  const displayRows = useMemo(() => {
    if (!rows.length) return rows;
    if (periodMode === 'daily')  return rows.filter(r => r.date && r.date.slice(0, 10) === focusDate);
    if (periodMode === 'weekly') return rows.filter(r => r.date && r.date.slice(0, 10) >= weekBounds.start && r.date.slice(0, 10) <= weekBounds.end);
    return rows;
  }, [rows, periodMode, focusDate, weekBounds]);

  // ── Days/Weeks filtered for overview matrix ───────────────────────────────
  const displayDays = useMemo(() => {
    if (!summaryDays.length) return summaryDays;
    if (periodMode === 'daily')
      return summaryDays.filter(d => d.date === focusDate);
    if (periodMode === 'weekly')
      return summaryDays.filter(d => d.date >= weekBounds.start && d.date <= weekBounds.end);
    return summaryDays;
  }, [summaryDays, periodMode, focusDate, weekBounds]);

  const displayWeeks = useMemo(() => {
    if (!summaryWeeks.length || periodMode === 'monthly') return summaryWeeks;
    if (periodMode === 'daily') return [];
    const focusWeek = getISOWeekNum(focusDate);
    return summaryWeeks.filter(w => w.iso_week === focusWeek || w.week_num === focusWeek);
  }, [summaryWeeks, periodMode, focusDate]);

  const periodLabel = useMemo(() => {
    if (periodMode === 'daily')
      return new Date(focusDate + 'T12:00:00').toLocaleDateString('sq-AL', { day: '2-digit', month: 'long', year: 'numeric' });
    if (periodMode === 'weekly')
      return `KW ${getISOWeekNum(focusDate)} — ${weekBounds.start.slice(8)}.${weekBounds.start.slice(5,7)}. – ${weekBounds.end.slice(8)}.${weekBounds.end.slice(5,7)}.`;
    return `${t('report.month_'+month)} ${year}`;
  }, [periodMode, focusDate, weekBounds, month, year, t]);

  // ── Shared slot helpers (mirror WorkScheduleMatrix) ──────────────────────
  const _slotHelpers = useMemo(() => {
    const h2n = t => { const [h, m] = (t || '').split(':').map(Number); return h * 60 + (m || 0); };
    const n2t = n => `${String(Math.floor(n / 60)).padStart(2,'0')}:${String(n % 60).padStart(2,'0')}`;
    const localMin = iso => { if (!iso) return null; const d = new Date(iso); return isNaN(d) ? null : d.getHours() * 60 + d.getMinutes(); };
    const ss = workerInfo?.schedule_start;
    const se = workerInfo?.schedule_end;
    // Start: schedule -1h, then clamp down to earliest actual check-in
    let sH = ss ? Math.max(5, parseInt(ss.split(':')[0], 10) - 1) : 6;
    // End: schedule +2h, then clamp up to latest actual check-out
    let eH = se ? Math.min(23, parseInt(se.split(':')[0], 10) + 2) : 20;
    rows.forEach(r => {
      if (r.check_in)  { const d = new Date(r.check_in);  if (!isNaN(d)) sH = Math.min(sH, Math.max(5, d.getHours())); }
      if (r.check_out) { const d = new Date(r.check_out); if (!isNaN(d)) eH = Math.max(eH, Math.min(23, d.getHours() + 1)); }
      (r.breaks || []).forEach(br => {
        if (br.end) { const d = new Date(br.end); if (!isNaN(d)) eH = Math.max(eH, Math.min(23, d.getHours() + 1)); }
      });
    });
    const slots = [];
    for (let t = sH * 60; t < eH * 60; t += 30) slots.push(t);
    const ssMin = ss ? h2n(ss) : null;
    const seMin = se ? h2n(se) : null;
    function slotType(slotMin, row) {
      if (!row.is_work_day) return 'dayoff';
      if (!row.check_in) {
        // Future scheduled day: show the planned window instead of a red row.
        if (row.compliance_status === 'scheduled' && ssMin !== null && seMin !== null)
          return (slotMin >= ssMin && slotMin < seMin) ? 'planned' : 'outside';
        return 'absent';
      }
      const ci = localMin(row.check_in);
      const co = row.check_out ? localMin(row.check_out) : 24 * 60;
      if (ci === null || slotMin < ci) return 'outside';
      if (co !== null && slotMin >= co) return 'outside';
      for (const br of (row.breaks || [])) {
        if (!br.start || !br.end) continue;
        const bs = localMin(br.start), be = localMin(br.end);
        if (bs !== null && be !== null && slotMin >= bs && slotMin < be) return 'break';
      }
      if (seMin !== null && slotMin >= seMin) return 'overtime';
      return 'work';
    }
    const getMonKey = ds => {
      const d = new Date(ds), off = d.getDay() === 0 ? 6 : d.getDay() - 1;
      const mon = new Date(d); mon.setDate(d.getDate() - off);
      return mon.toISOString().slice(0, 10);
    };
    const getISOWeek = ds => {
      const d = new Date(ds); d.setHours(0,0,0,0);
      d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
      const w1 = new Date(d.getFullYear(), 0, 4);
      return 1 + Math.round(((d - w1) / 86400000 - 3 + (w1.getDay() + 6) % 7) / 7);
    };
    return { slots, ssMin, seMin, slotType, getMonKey, getISOWeek, n2t };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerInfo, loaded]);

  // ── Export Excel (matrix with colors via xlsx-js-style) ───────────────────
  function exportExcel() {
    const { slots, ssMin, seMin, slotType, getMonKey, getISOWeek } = _slotHelpers;
    const wn = workerFullName(workerInfo);
    const FIXED = 6; // KW, Date, Day, Orari, Projekti, NP
    const totalCols = FIXED + slots.length;

    // Per-day project text: "ProjA 07:00-11:00 · ProjB 11:00-15:00"
    const projText = (row) => (row.projects || [])
      .map(p => p.start ? `${p.name} ${p.start}-${p.end}` : (p.hours ? `${p.name} ${p.hours}h` : p.name))
      .join(' · ');

    // Cell style helpers
    const cs = (rgb, fontRgb, bold, sz = 7, halign = 'center') => ({
      fill: rgb ? { patternType: 'solid', fgColor: { rgb } } : undefined,
      font: { bold, sz, color: { rgb: fontRgb || '333333' } },
      alignment: { horizontal: halign, vertical: 'center', wrapText: false },
      border: {
        top:    { style: 'thin', color: { rgb: 'D0D0D0' } },
        bottom: { style: 'thin', color: { rgb: 'D0D0D0' } },
        left:   { style: 'thin', color: { rgb: 'D0D0D0' } },
        right:  { style: 'thin', color: { rgb: 'D0D0D0' } },
      },
    });
    const SLOT_RGB = { work:'1565C0', overtime:'7B1FA2', break:'F9A825', absent:'FFCDD2', planned:'E3F2FD', dayoff:'F5F5F5', outside:'FFFFFF' };

    // KPI totals
    const kd  = rows.filter(r => r.is_work_day);
    const kPl = kd.reduce((s,r)=>s+(Number(r.planned_minutes)||0),0);
    const kNt = kd.reduce((s,r)=>s+(Number(r.net_worked_minutes)||0),0);
    const kOT = kd.reduce((s,r)=>s+(Number(r.overtime_minutes)||0),0);
    const kLt = kd.reduce((s,r)=>s+(Number(r.late_minutes)||0),0);
    const kAb = kd.filter(r=>r.compliance_status==='absent').length;
    const kWE = rows.filter(r=>!r.is_work_day&&r.check_in).reduce((s,r)=>s+(Number(r.net_worked_minutes)||0),0);

    const supTxt  = workerInfo?.supervisor_name ? `   ${t('report.supervisor')}: ${workerInfo.supervisor_name}` : '';
    const projTxt = (workerInfo?.projects || []).length
      ? `   ${t('report.projects')}: ` + workerInfo.projects.map(p => `${p.name} ${fmtHM((Number(p.hours)||0)*60)}`).join(', ')
      : '';

    const aoa = [
      [`${t('report.monthlyScheduleTitle').toUpperCase()} — ${wn} — ${t('report.month_'+(month))} ${year}`],
      [`${t('report.planned')}: ${fmtHM(kPl)}   ${t('report.workedHours')}: ${fmtHM(kNt)}   ${t('report.difference')}: ${fmtDiff(kNt-kPl)}   OT: ${fmtHM(kOT)}   WE: ${fmtHM(kWE)}   ${t('report.lateLabel')}: ${kLt}m   ${t('report.absentAbbr')}: ${kAb}d${supTxt}${projTxt}`],
      [],
    ];
    const styles = {}; // rowIdx → colIdx → style object

    // Header row (row index 3)
    const HDR_ROW = 3;
    aoa.push(['KW','Data','Dita','Orari','Projekti','NP', ...slots.map(t => {
      const h = Math.floor(t/60), m = t%60;
      return m === 0 ? `${String(h).padStart(2,'0')}:00` : ':30';
    })]);
    styles[HDR_ROW] = {};
    for (let c = 0; c < FIXED; c++)
      styles[HDR_ROW][c] = cs('1565C0','FFFFFF',true,7,'center');
    for (let c = FIXED; c < totalCols; c++) {
      const t = slots[c - FIXED];
      const bg = ssMin === t ? '1565C0' : seMin === t ? '7B1FA2' : 'F0F0F0';
      const fg = (ssMin === t || seMin === t) ? 'FFFFFF' : '666666';
      styles[HDR_ROW][c] = cs(bg, fg, true, 6, 'center');
    }

    // Data rows
    let prevKey = null, wkBuf = [], wkNum = null;

    const flushWeek = () => {
      if (!wkBuf.length) return;
      const wkd = wkBuf.filter(r => r.is_work_day);
      const net  = wkd.reduce((s,r)=>s+(Number(r.net_worked_minutes)||0),0);
      const plan = wkd.reduce((s,r)=>s+(Number(r.planned_minutes)||0),0);
      const ot   = wkd.reduce((s,r)=>s+(Number(r.overtime_minutes)||0),0);
      const we   = wkBuf.filter(r=>!r.is_work_day&&r.check_in).reduce((s,r)=>s+(Number(r.net_worked_minutes)||0),0);
      const reg  = Math.max(0, net - ot);
      const ri = aoa.length;
      aoa.push([`KW${wkNum}`, `Reg: ${fmtHM(reg)}`, `OT: ${fmtHM(ot)}`, `Plan: ${fmtHM(plan)}`, '', `WE: ${fmtHM(we)}`, ...slots.map(() => '')]);
      styles[ri] = {};
      for (let c = 0; c < totalCols; c++) styles[ri][c] = cs('E8EDF2','1565C0',true,7,'left');
      styles[ri][0] = cs('E8EDF2','1565C0',true,7,'center'); // KW centered
      wkBuf = [];
    };

    let prevMonday = null;
    rows.forEach(row => {
      const mk = getMonKey(row.date);
      if (prevMonday !== null && mk !== prevMonday) flushWeek();
      prevMonday = mk;
      wkNum = getISOWeek(row.date);
      wkBuf.push(row);

      const isOff = !row.is_work_day;
      const isFirstOfWeek = wkBuf.length === 1;
      const npCode = !row.is_work_day ? '—' : row.compliance_status === 'absent' ? 'A' : '';
      const orari  = row.schedule_start && row.schedule_end
        ? `${String(row.schedule_start).slice(0,5)}–${String(row.schedule_end).slice(0,5)}` : '';
      const dateFmt = `${row.date.slice(8)}.${row.date.slice(5,7)}.`;
      const ri = aoa.length;

      aoa.push([isFirstOfWeek ? wkNum : '', dateFmt, row.day_short, orari, projText(row), npCode, ...slots.map(() => '')]);
      styles[ri] = {};

      // Fixed-column styles
      const fixBg = isOff ? 'F5F5F5' : row.compliance_status==='absent' ? 'FFEBEB' : row.is_adjusted ? 'F3E5F5' : null;
      const fixFg = isOff ? 'AAAAAA' : null;
      for (let c = 0; c < FIXED; c++) styles[ri][c] = cs(fixBg, fixFg, c===0, 7, 'center');
      styles[ri][4] = cs(fixBg, fixFg, false, 6, 'left'); // Projekti — left-aligned, smaller

      // Slot-column styles
      for (let c = FIXED; c < totalCols; c++) {
        const t = slots[c - FIXED];
        const type = slotType(t, row);
        const rgb = SLOT_RGB[type] || 'FFFFFF';
        styles[ri][c] = cs(rgb, null, false, 6, 'center');
      }
    });
    flushWeek();

    // Build worksheet
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Apply styles
    Object.entries(styles).forEach(([ri, cols]) =>
      Object.entries(cols).forEach(([ci, s]) => {
        const addr = XLSX.utils.encode_cell({ r: Number(ri), c: Number(ci) });
        if (!ws[addr]) ws[addr] = { t: 's', v: '' };
        ws[addr].s = s;
      })
    );

    // Column widths
    ws['!cols'] = [
      { wch: 5 }, { wch: 9 }, { wch: 5 }, { wch: 11 }, { wch: 22 }, { wch: 3 },
      ...slots.map(() => ({ wch: 3.2 })),
    ];
    ws['!merges'] = [
      { s: { r:0, c:0 }, e: { r:0, c:totalCols-1 } },
      { s: { r:1, c:0 }, e: { r:1, c:totalCols-1 } },
    ];
    ws['!rows'] = Array.from({ length: aoa.length }, () => ({ hpt: 14 }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Orari Mujor');
    XLSX.writeFile(wb, `orari-mujor-${fWorker}-${year}-${String(month).padStart(2,'0')}.xlsx`);
  }

  // ── Export PDF (drawn colored matrix, A4 landscape) ───────────────────────
  function exportPdf() {
    const { slots, ssMin, seMin, slotType, getMonKey, getISOWeek, n2t } = _slotHelpers;
    const wn  = workerFullName(workerInfo);
    const doc = new jsPDF({ orientation: 'landscape', format: 'a4', unit: 'mm' });

    // Layout constants
    const ML = 5, MR = 5, MT = 5;
    const PW = 297;
    const avW = PW - ML - MR; // 287mm
    const COL = { kw:6, date:13, day:6, sched:14, proj:32, np:4 };
    const fixW   = COL.kw + COL.date + COL.day + COL.sched + COL.proj + COL.np;
    const slotW  = Math.max(3.5, Math.min(10, (avW - fixW) / Math.max(slots.length, 1)));

    // Per-day project text + a clip helper (PDF cells are narrow).
    const projText = (row) => (row.projects || [])
      .map(p => p.start ? `${p.name} ${p.start}-${p.end}` : (p.hours ? `${p.name} ${p.hours}h` : p.name))
      .join(' · ');
    const clip = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + '…' : (s || ''));
    const HDR1_H = 7, HDR2_H = 4, ROW_H = 4.3, WEEK_H = 5;

    // Helper: filled+bordered rectangle
    const fillRect = (x, y, w, h, rgbArr) => {
      if (rgbArr) { doc.setFillColor(...rgbArr); doc.rect(x, y, w, h, 'F'); }
      doc.setDrawColor(200, 200, 200);
      doc.rect(x, y, w, h, 'S');
    };
    const drawText = (text, x, y, w, h, opts = {}) => {
      if (!text) return;
      doc.setFontSize(opts.sz || 6.5);
      doc.setFont(undefined, opts.bold ? 'bold' : 'normal');
      doc.setTextColor(...(opts.color || [50, 50, 50]));
      const tx = opts.align === 'center' ? x + w / 2 : x + 1;
      doc.text(String(text), tx, y + h * 0.68, { align: opts.align === 'center' ? 'center' : 'left', maxWidth: w - 1 });
    };

    // ── Page header ──
    let y = MT;
    doc.setFontSize(10); doc.setFont(undefined, 'bold'); doc.setTextColor(21, 101, 192);
    doc.text(t('report.monthlyScheduleTitle').toUpperCase(), ML, y + 4.5);
    doc.setFontSize(7.5); doc.setFont(undefined, 'normal'); doc.setTextColor(60, 60, 60);
    doc.text(
      `${wn}  ·  ${t('report.month_'+month)} ${year}  ·  ${workerInfo?.department_name||''}  ·  ${workerInfo?.schedule_name||''}` +
      (workerInfo?.schedule_start ? `  (${String(workerInfo.schedule_start).slice(0,5)}–${String(workerInfo.schedule_end).slice(0,5)})` : '') +
      (workerInfo?.supervisor_name ? `  ·  ${t('report.supervisor')}: ${workerInfo.supervisor_name}` : ''),
      ML, y + 9.5
    );

    // KPI strip
    const kd  = rows.filter(r => r.is_work_day);
    const kPl = kd.reduce((s,r)=>s+(Number(r.planned_minutes)||0),0);
    const kNt = kd.reduce((s,r)=>s+(Number(r.net_worked_minutes)||0),0);
    const kOT = kd.reduce((s,r)=>s+(Number(r.overtime_minutes)||0),0);
    const kLt = kd.reduce((s,r)=>s+(Number(r.late_minutes)||0),0);
    const kAb = kd.filter(r=>r.compliance_status==='absent').length;
    const kWE = rows.filter(r=>!r.is_work_day&&r.check_in).reduce((s,r)=>s+(Number(r.net_worked_minutes)||0),0);
    doc.setFontSize(7); doc.setTextColor(40, 40, 40);
    const pdfProjTxt = (workerInfo?.projects || []).length
      ? `   ${t('report.projects')}: ` + workerInfo.projects.map(p => `${p.name} ${fmtHM((Number(p.hours)||0)*60)}`).join(', ')
      : '';
    doc.text(
      `${t('report.planned')}: ${fmtHM(kPl)}   ${t('report.workedHours')}: ${fmtHM(kNt)}   ${t('report.difference')}: ${fmtDiff(kNt-kPl)}   OT: ${fmtHM(kOT)}   WE: ${fmtHM(kWE)}   ${t('report.lateLabel')}: ${kLt}m   ${t('report.absentAbbr')}: ${kAb}d${pdfProjTxt}`,
      ML, y + 15
    );
    y += 20;

    // ── Matrix header row 1 — fixed col labels + hour spans ──
    const fixedCols = [
      [COL.kw, 'KW'], [COL.date, 'Data'], [COL.day, 'Dita'], [COL.sched, 'Orari'], [COL.proj, t('report.project')], [COL.np, 'NP'],
    ];
    let x = ML;
    fixedCols.forEach(([w, lbl]) => {
      fillRect(x, y, w, HDR1_H, [21, 101, 192]);
      drawText(lbl, x, y, w, HDR1_H, { sz:6.5, bold:true, color:[255,255,255], align:'center' });
      x += w;
    });
    // Hour spans
    let si = 0;
    while (si < slots.length) {
      const t = slots[si];
      if (t % 60 === 0) {
        let j = si + 1;
        while (j < slots.length && slots[j] % 60 !== 0) j++;
        const spanW = (j - si) * slotW;
        const isSS = ssMin === t, isSE = seMin === t;
        const bg = isSS ? [21,101,192] : isSE ? [123,31,162] : [232,232,232];
        const fg = (isSS||isSE) ? [255,255,255] : [80,80,80];
        fillRect(x, y, spanW, HDR1_H, bg);
        drawText(n2t(t), x, y, spanW, HDR1_H, { sz:6, bold:true, color:fg, align:'center' });
        x += spanW; si = j;
      } else {
        fillRect(x, y, slotW, HDR1_H, [240,240,240]);
        x += slotW; si++;
      }
    }
    y += HDR1_H;

    // ── Header row 2 — tick marks ──
    x = ML + fixW;
    for (let k = 0; k < slots.length; k++) {
      const isH = slots[k] % 60 === 0;
      fillRect(x, y, slotW, HDR2_H, isH ? [220,220,220] : [245,245,245]);
      drawText(isH ? '|' : '·', x, y, slotW, HDR2_H, { sz:5, color:[160,160,160], align:'center' });
      x += slotW;
    }
    y += HDR2_H;

    // ── Data rows ──
    const SLOT_RGB_PDF = {
      work:     [21,  101, 192],
      overtime: [123, 31,  162],
      break:    [249, 168, 37 ],
      absent:   [255, 205, 210],
      planned:  [227, 242, 253],
      dayoff:   [245, 245, 245],
      outside:  [255, 255, 255],
    };

    let prevKey = null, wkBuf = [], wkNum = null;

    const drawWeekRow = (wrs, wn2) => {
      if (!wrs.length) return;
      const wkd  = wrs.filter(r => r.is_work_day);
      const net  = wkd.reduce((s,r)=>s+(Number(r.net_worked_minutes)||0),0);
      const plan = wkd.reduce((s,r)=>s+(Number(r.planned_minutes)||0),0);
      const ot   = wkd.reduce((s,r)=>s+(Number(r.overtime_minutes)||0),0);
      const we   = wrs.filter(r=>!r.is_work_day&&r.check_in).reduce((s,r)=>s+(Number(r.net_worked_minutes)||0),0);
      const reg  = Math.max(0, net - ot);
      const bg   = [232, 237, 242];
      let xw = ML;
      fillRect(xw, y, COL.kw,    WEEK_H, bg); drawText(`KW${wn2}`, xw, y, COL.kw,    WEEK_H, {sz:6,bold:true,color:[21,101,192],align:'center'}); xw+=COL.kw;
      fillRect(xw, y, COL.date,  WEEK_H, bg); drawText(`Reg: ${fmtHM(reg)}`, xw, y, COL.date, WEEK_H, {sz:5.5,bold:true,color:[21,101,192]}); xw+=COL.date;
      fillRect(xw, y, COL.day,   WEEK_H, bg); drawText(`OT: ${fmtHM(ot)}`,  xw, y, COL.day,  WEEK_H, {sz:5.5,bold:true,color:[123,31,162]}); xw+=COL.day;
      fillRect(xw, y, COL.sched, WEEK_H, bg); drawText(`Pl: ${fmtHM(plan)}`, xw, y, COL.sched,WEEK_H, {sz:5.5,bold:true,color:[46,125,50]}); xw+=COL.sched;
      fillRect(xw, y, COL.proj,  WEEK_H, bg); xw+=COL.proj;
      fillRect(xw, y, COL.np,    WEEK_H, bg); drawText(`WE:${fmtHM(we)}`, xw, y, COL.np,   WEEK_H, {sz:4.5,bold:true,color:[179,98,0]}); xw+=COL.np;
      for (let k = 0; k < slots.length; k++) { fillRect(xw, y, slotW, WEEK_H, bg); xw += slotW; }
      y += WEEK_H;
    };

    rows.forEach(row => {
      const mk = getMonKey(row.date);
      if (prevKey !== null && mk !== prevKey) { drawWeekRow(wkBuf, wkNum); wkBuf = []; }
      prevKey = mk;
      wkNum   = getISOWeek(row.date);
      const isFirstOfWeek = wkBuf.length === 0;
      wkBuf.push(row);

      const isOff = !row.is_work_day;
      const rowBg = isOff ? [250,250,250] : row.compliance_status==='absent' ? [255,235,235] : null;
      const npCode = !row.is_work_day ? '—' : row.compliance_status==='absent' ? 'A' : '';
      const orari  = row.schedule_start && row.schedule_end
        ? `${String(row.schedule_start).slice(0,5)}–${String(row.schedule_end).slice(0,5)}` : '';
      const dateFmt = `${row.date.slice(8)}.${row.date.slice(5,7)}.`;
      const tfg     = isOff ? [180,180,180] : [40,40,40];

      let xr = ML;
      // KW cell
      if (rowBg) { doc.setFillColor(...rowBg); doc.rect(xr, y, COL.kw, ROW_H, 'F'); }
      doc.setDrawColor(200,200,200); doc.rect(xr, y, COL.kw, ROW_H, 'S');
      if (isFirstOfWeek) drawText(String(wkNum), xr, y, COL.kw, ROW_H, {sz:5.5,bold:true,color:[21,101,192],align:'center'});
      xr += COL.kw;
      // Date
      fillRect(xr, y, COL.date, ROW_H, rowBg); drawText(dateFmt, xr, y, COL.date, ROW_H, {sz:6,color:tfg,align:'center'}); xr+=COL.date;
      // Day
      fillRect(xr, y, COL.day,  ROW_H, rowBg); drawText(row.day_short, xr, y, COL.day, ROW_H, {sz:6,color:tfg,align:'center'}); xr+=COL.day;
      // Schedule
      fillRect(xr, y, COL.sched,ROW_H, rowBg); drawText(orari, xr, y, COL.sched, ROW_H, {sz:5.5,color:tfg,align:'center'}); xr+=COL.sched;
      // Projekti (planned per-day project + time block)
      fillRect(xr, y, COL.proj, ROW_H, rowBg); drawText(clip(projText(row), 34), xr, y, COL.proj, ROW_H, {sz:4.2,color:tfg}); xr+=COL.proj;
      // NP
      fillRect(xr, y, COL.np,   ROW_H, rowBg); drawText(npCode, xr, y, COL.np, ROW_H, {sz:6,bold:true,color:npCode==='A'?[194,18,18]:tfg,align:'center'}); xr+=COL.np;
      // Slots
      for (let k = 0; k < slots.length; k++) {
        const t    = slots[k];
        const type = slotType(t, row);
        const rgb  = SLOT_RGB_PDF[type] || [255,255,255];
        doc.setFillColor(...rgb);
        doc.rect(xr, y, slotW, ROW_H, 'F');
        // Schedule boundary markers
        if (ssMin === t) { doc.setDrawColor(21,101,192);  doc.setLineWidth(0.4); doc.line(xr, y, xr, y+ROW_H); doc.setLineWidth(0.1); }
        if (seMin === t) { doc.setDrawColor(123,31,162);  doc.setLineWidth(0.4); doc.line(xr, y, xr, y+ROW_H); doc.setLineWidth(0.1); }
        doc.setDrawColor(220,220,220); doc.rect(xr, y, slotW, ROW_H, 'S');
        xr += slotW;
      }
      y += ROW_H;
    });
    drawWeekRow(wkBuf, wkNum); // flush last week

    doc.save(`orari-mujor-${fWorker}-${year}-${String(month).padStart(2,'0')}.pdf`);
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ px: { xs: 0.5, md: 1 }, pt: 1, pb: 4 }}>

      {/* ── Compact toolbar ─────────────────────────────────────────────── */}
      <Paper
        variant="outlined"
        sx={{
          mb: 1.5,
          px: 1.5, py: 0.75,
          display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
          borderRadius: 1.5,
        }}
      >
        {/* Back button — only in individual mode for admins */}
        {viewMode === 'individual' && canViewOthers && (
          <>
            <Tooltip title="Ktheu te të gjithë punëtorët">
              <Button
                size="small" startIcon={<ArrowBackIcon />}
                onClick={backToOverview}
                variant="outlined"
                sx={{ minWidth: 'auto', px: 1 }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  {t('report.all')}
                </Box>
              </Button>
            </Tooltip>
            <Divider orientation="vertical" flexItem />
          </>
        )}

        {/* View mode toggle for admins */}
        {canViewOthers && (
          <>
            <Tooltip title={viewMode === 'overview' ? 'Të gjithë punëtorët' : 'Individual'}>
              <IconButton
                size="small"
                onClick={() => viewMode === 'overview' ? undefined : backToOverview()}
                sx={{
                  color: viewMode === 'overview' ? 'primary.main' : 'text.secondary',
                  bgcolor: viewMode === 'overview' ? 'primary.50' : 'transparent',
                }}
              >
                <PeopleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}

        {/* Worker selector — only in individual mode */}
        {viewMode === 'individual' && canViewOthers && (
          <TextField
            select size="small" label={t('report.worker')}
            value={fWorker}
            onChange={e => setFWorker(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">— {t('report.selectWorker')} —</MenuItem>
            {filteredWorkers.map(w => (
              <MenuItem key={w.Id || w.id} value={String(w.Id || w.id)}>
                {`${w.FirstName || w.first_name || ''} ${w.LastName || w.last_name || ''}`.trim()}
              </MenuItem>
            ))}
          </TextField>
        )}

        {/* Departamenti — vetëm te View 1. Te orari individual punonjësi është
            zgjedhur tashmë; një filtër departamenti aty do të filtronte një
            listë me një rresht, dhe prekja e tij do ta zbrazte zgjedhjen.
            Vlera nuk fshihet: kthimi te View 1 e gjen ashtu si e la. */}
        {viewMode === 'overview' && (
          <TextField
            select size="small" label={t('report.department')}
            value={fDept}
            onChange={e => setFDept(e.target.value)}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">{t('report.all')}</MenuItem>
            {depts.map(d => (
              <MenuItem key={d.id || d.Id} value={String(d.id || d.Id)}>
                {d.name || d.Name}
              </MenuItem>
            ))}
          </TextField>
        )}

        {/* Project filter */}
        <TextField
          select size="small" label={t('report.project')}
          value={fProject}
          onChange={e => { setFProject(e.target.value); if (viewMode === 'individual') setFWorker(''); }}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">{t('report.allProjects')}</MenuItem>
          {projects.map(p => (
            <MenuItem key={p.id || p.Id} value={String(p.id || p.Id)}>
              {p.name || p.Name}
            </MenuItem>
          ))}
        </TextField>

        <Divider orientation="vertical" flexItem />

        {/* Month navigation */}
        <Tooltip title={t('report.prevMonth')}>
          <IconButton size="small" onClick={prevMonth}><ChevronLeftIcon fontSize="small" /></IconButton>
        </Tooltip>

        <TextField
          select size="small" value={month}
          onChange={e => setMonth(Number(e.target.value))}
          sx={{ width: 110 }}
        >
          {Array.from({ length: 12 }, (_, i) => (
            <MenuItem key={i+1} value={i+1}>{t('report.month_'+(i+1))}</MenuItem>
          ))}
        </TextField>

        <TextField
          select size="small" value={year}
          onChange={e => setYear(Number(e.target.value))}
          sx={{ width: 80 }}
        >
          {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>

        <Tooltip title={t('report.nextMonth')}>
          <IconButton size="small" onClick={nextMonth}><ChevronRightIcon fontSize="small" /></IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem />

        {/* Period mode toggle: Ditor | Javor | Mujor */}
        <ToggleButtonGroup
          value={periodMode}
          exclusive
          size="small"
          onChange={(_, v) => handlePeriodMode(v)}
          sx={{
            '& .MuiToggleButton-root': {
              px: 1.5, py: 0.3,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.78rem',
              lineHeight: 1.4,
            },
          }}
        >
          <ToggleButton value="daily">Ditor</ToggleButton>
          <ToggleButton value="weekly">Javor</ToggleButton>
          <ToggleButton value="monthly">Mujor</ToggleButton>
        </ToggleButtonGroup>

        {/* Day / Week sub-navigation */}
        {(periodMode === 'daily' || periodMode === 'weekly') && (
          <>
            <Tooltip title={periodMode === 'daily' ? 'Dita e mëparshme' : 'Java e mëparshme'}>
              <IconButton size="small" onClick={periodMode === 'daily' ? prevDay : prevWeek}>
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="caption" sx={{
              fontWeight: 700, whiteSpace: 'nowrap',
              minWidth: periodMode === 'weekly' ? 110 : 90,
              textAlign: 'center', fontSize: '0.75rem',
            }}>
              {periodMode === 'daily'
                ? new Date(focusDate).toLocaleDateString('sq-AL', { day: '2-digit', month: 'short', year: 'numeric' })
                : `KW ${getISOWeekNum(focusDate)} ${weekBounds.start.slice(5).replace('-', '.')}–${weekBounds.end.slice(5).replace('-', '.')}`
              }
            </Typography>
            <Tooltip title={periodMode === 'daily' ? 'Dita tjetër' : 'Java tjetër'}>
              <IconButton size="small" onClick={periodMode === 'daily' ? nextDay : nextWeek}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}

        {/* Loading indicator */}
        {(loading || summaryLoading) && <CircularProgress size={18} thickness={4} />}

        <Box sx={{ flex: 1 }} />

        {/* Export buttons — visible when individual loaded */}
        {viewMode === 'individual' && loaded && (
          <>
            {/* Butoni i rrjetës së detajuar u hoq bashkë me vetë atë rrjetë: hapte
                një rrugë shkrimi («Kërko korrigjim») nga një ekran vetëm-shikim.
                Eksportet më poshtë mbeten — ato lexojnë, nuk ndryshojnë. */}
            <Tooltip title="Eksporto Excel">
              <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportExcel}
                sx={{ minWidth: { xs: 36, sm: 'auto' }, px: { xs: 0.5, sm: 1.5 } }}>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Excel</Box>
              </Button>
            </Tooltip>
            <Tooltip title="PDF (A4 landscape)">
              <Button variant="outlined" size="small" startIcon={<PictureAsPdfIcon />} onClick={exportPdf}
                sx={{ minWidth: { xs: 36, sm: 'auto' }, px: { xs: 0.5, sm: 1.5 } }}>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>PDF</Box>
              </Button>
            </Tooltip>
            <Tooltip title="Printo">
              <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={() => window.print()}
                sx={{ minWidth: { xs: 36, sm: 'auto' }, px: { xs: 0.5, sm: 1.5 } }}>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Print</Box>
              </Button>
            </Tooltip>
          </>
        )}

        <Tooltip title="Reset">
          <IconButton size="small" onClick={handleReset}><RestartAltIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Paper>

      {/* ── Overview mode — all workers table ───────────────────────────── */}
      {viewMode === 'overview' && canViewOthers && (
        <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
          <Box sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#f5f7fa', display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 13 }}>
              {t('report.monthlyScheduleTitle')} — {periodLabel}
            </Typography>
            {!summaryLoading && summaryData.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                ({fDept ? (summaryData.filter(w => String(w.department_id) === String(fDept)).length) : summaryData.length} {t('report.worker').toLowerCase()})
              </Typography>
            )}
          </Box>
          <AllWorkersMatrix
            data={summaryData}
            days={displayDays}
            weeks={displayWeeks}
            loading={summaryLoading}
            onSelect={handleSelectWorker}
            onDayClick={(worker, date, day) => setDayDetailDlg({ worker, date, day })}
            fDept={fDept}
          />
        </Paper>
      )}

      {/* ── Day detail dialog ────────────────────────────────────────────── */}
      <Dialog
        open={!!dayDetailDlg}
        onClose={() => setDayDetailDlg(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {dayDetailDlg && (() => {
          const { worker, date, day } = dayDetailDlg;
          const workerName = `${worker.first_name || ''} ${worker.last_name || ''}`.trim();
          const isAbsent   = day.status === 'absent';
          const isSched    = day.status === 'scheduled';
          const isLate     = day.late_minutes > 0;
          const statusColor = isAbsent ? '#c62828' : isSched ? '#5b7fa6' : isLate ? '#e65100' : '#2e7d32';
          const statusLabel = isAbsent ? 'Mungon' : isSched ? 'E planifikuar' : isLate ? `Vonë ${day.late_minutes} min` : 'Prezent';
          const ciStr = day.check_in  ? String(day.check_in).slice(0,5)  : null;
          const coStr = day.check_out ? String(day.check_out).slice(0,5) : null;
          const workedH = day.net_worked_minutes ? Math.floor(day.net_worked_minutes / 60) : 0;
          const workedM = day.net_worked_minutes ? day.net_worked_minutes % 60 : 0;
          const workedStr = day.net_worked_minutes ? `${workedH}h ${workedM}m` : '—';
          const plannedH = day.planned_minutes ? Math.floor(day.planned_minutes / 60) : 0;
          const plannedM = day.planned_minutes ? day.planned_minutes % 60 : 0;
          const plannedStr = day.planned_minutes ? `${plannedH}h ${plannedM}m` : '—';
          return (
            <>
              <DialogTitle sx={{ pb: 0.5, fontWeight: 700 }}>
                {workerName}
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, mt: 0.25 }}>
                  {date}
                </Typography>
              </DialogTitle>
              <DialogContent sx={{ pt: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      px: 1.5, py: 0.4, borderRadius: 2,
                      background: statusColor + '18',
                      color: statusColor, fontWeight: 700, fontSize: 13,
                    }}>
                      {statusLabel}
                    </Box>
                  </Box>
                  {!isAbsent && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <Box sx={{ p: 1.5, borderRadius: 2, background: '#f0f7f2', textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary" display="block">Hyrja</Typography>
                        <Typography fontWeight={700} fontSize={18} color="#2e6e44">{ciStr || '—'}</Typography>
                      </Box>
                      <Box sx={{ p: 1.5, borderRadius: 2, background: '#fdf3f3', textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary" display="block">Dalja</Typography>
                        <Typography fontWeight={700} fontSize={18} color="#7a3030">{coStr || '—'}</Typography>
                      </Box>
                      <Box sx={{ p: 1.5, borderRadius: 2, background: '#f4f6fb', textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary" display="block">Orë punuar</Typography>
                        <Typography fontWeight={700} fontSize={16}>{workedStr}</Typography>
                      </Box>
                      <Box sx={{ p: 1.5, borderRadius: 2, background: '#f4f6fb', textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary" display="block">Orë planifikuar</Typography>
                        <Typography fontWeight={700} fontSize={16}>{plannedStr}</Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
                <Button onClick={() => setDayDetailDlg(null)} variant="outlined" size="small">Mbyll</Button>
                <Button
                  onClick={() => { handleSelectWorker(worker.worker_id); setDayDetailDlg(null); }}
                  variant="contained" size="small"
                >
                  Hap kartelën
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* ── Individual mode ──────────────────────────────────────────────── */}
      {viewMode === 'individual' && (
        <>
          {/* No worker selected — show a sample preview so the user sees what the report looks like */}
          {!loading && !loaded && !fWorker && (
            <Box>
              <Alert
                severity="info"
                icon={<PersonIcon />}
                sx={{ mb: 1.5, borderRadius: 1.5, '& .MuiAlert-message': { width: '100%' } }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.5} sx={{ width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {t('report.samplePreviewTitle')} —
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('report.samplePreviewHint')}
                  </Typography>
                </Stack>
              </Alert>

              <Box sx={{ opacity: 0.92, pointerEvents: 'none', userSelect: 'none' }} aria-hidden="true">
                <SummaryBar
                  rows={SAMPLE_ROWS}
                  worker={{ ...SAMPLE_WORKER, first_name: t('report.sampleWorkerName'), last_name: '', schedule_name: t('report.sampleScheduleName') }}
                  year={2026}
                  month={3}
                />
                <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden', mt: 1 }}>
                  <MatrixLegend />
                  <WorkScheduleMatrix
                    rows={SAMPLE_ROWS}
                    scheduleStart={SAMPLE_WORKER.schedule_start}
                    scheduleEnd={SAMPLE_WORKER.schedule_end}
                    isAdmin={false}
                    readOnly
                    slotEdits={{}}
                  />
                </Paper>
              </Box>

              <Box sx={{ pt: 1.5, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body2">{t('report.selectWorkerToLoadSchedule')}</Typography>
              </Box>
            </Box>
          )}

          {/* Loaded report */}
          {loaded && (
            <>
              {/* Summary strip */}
              <SummaryBar rows={displayRows} worker={workerInfo} year={year} month={month} />

              {/* Dense matrix */}
              <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
                <MatrixLegend />
                {/* VETËM SHIKIM. `readOnly` nuk është vetëm mungesë handler-ash:
                    rrjeta nuk e vizaton fare kolonën e korrigjimit, ndaj nuk
                    mbetet asnjë kontroll që të premtojë ndryshim. Slot-et e
                    hequra shfaqen ende — të dhëna, jo veprime. */}
                <WorkScheduleMatrix
                  rows={displayRows}
                  scheduleStart={workerInfo?.schedule_start || null}
                  scheduleEnd={workerInfo?.schedule_end   || null}
                  isAdmin={false}
                  readOnly
                  slotEdits={slotEdits}
                />
                {displayRows.length === 0 && (
                  <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">{t('report.noDataForPeriod')}</Typography>
                  </Box>
                )}
              </Paper>
            </>
          )}
        </>
      )}

      {/* Këtu qëndronin tre dialogje që shkruanin: korrigjimi i ditës, korrigjimi
          i një slot-i dhe rrjeta e detajuar e orëve me butonin «Kërko korrigjim».
          Orari Individual Mujor nuk shkruan më asgjë — kërkesat për korrigjim
          bëhen te Qendra e Aprovimeve, ku kalojnë nga një aprovim. */}
    </Box>
  );
}

